import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Formik, Field } from 'formik';
import { Table, Breadcrumb, Form, Button, InputGroup } from 'react-bootstrap';
import ReactQuill from 'react-quill';
import { AiOutlinePlusCircle } from 'react-icons/ai';
import { FiInfo, FiTrash2 } from 'react-icons/fi';
import DatePicker from 'react-datepicker';
import dayjs from 'dayjs';

// import { useAuth } from 'Context/auth';
import { useUserStore } from 'Store/userStore';
import { CLIAM_APPID } from 'Utils/envLoader';
import CustomModal from 'Components/UI/CustomModal';
import UrgencySquareIcon from 'Components/UI/UrgencyIcon';

import NotificationService from 'Services/NotificationService';
import EventService from 'Services/EventService';

const schema = yup.object().shape(
    {
        eventHandle: yup.string().required(),
        appId: yup.string().required(),
        originUserId: yup.string().required(),
        title: yup.string().required(),
        content: yup.string().required(),
        actionUrl: yup.lazy(() =>
            yup
                .string()
                .nullable()
                .when('actionName', {
                    is: (actionName) => !!actionName,
                    then: yup
                        .string()
                        .matches(
                            /^((http|https):\/\/)?(www.)?(?!.*(http|https|www.))[a-zA-Z0-9_-]+(\.[a-zA-Z]+)+((\/)[\w#]+)*(\/\w+\?[a-zA-Z0-9_]+=\w+(&[a-zA-Z0-9_]+=\w+)*)?$/gm,
                        )
                        .required(),
                }),
        ),
        actionName: yup.lazy(() =>
            yup
                .string()
                .nullable()
                .when('actionUrl', {
                    is: (actionUrl) => !!actionUrl,
                    then: yup.string().required(),
                }),
        ),
        urgency: yup.string().required(),
        byAdmin: yup.boolean().nullable(),
        dateDisplay: yup.date().nullable(),
    },
    [['actionName', 'actionName']],
);

const AdminNotificationsIndex = () => {
    // const { user } = useAuth();
    const user = useUserStore((state) => state.user);

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showFormModal, setShowFormModal] = useState(false);
    const [events, setEvents] = useState([]);
    const [notificationsGroupIndex, setNotificationsGroupIndex] = useState(-1);
    const [notificationsGroup, setNotificationsGroup] = useState(null);
    const [notificationsGroups, setNotificationsGroups] = useState([]);

    useEffect(() => {
        loadNotifications();

        EventService.getEvents()
            .then((result) => {
                if (result.status === 200) {
                    setEvents(result.data);
                }
            })
            .catch();
    }, []);

    const loadNotifications = () => {
        NotificationService.getNotificationsByAdmins()
            .then((result) => {
                if (result.status === 200) {
                    setNotificationsGroups(result.data);
                }
            })
            .catch();
    };

    const handleDeleteModalClose = () => {
        setNotificationsGroup(null);
        setNotificationsGroupIndex(-1);
        setShowDeleteModal(false);
    };

    const handleDeleteModalOpen = (data, i) => {
        setNotificationsGroupIndex(i);
        setNotificationsGroup(data);
        setShowDeleteModal(true);
    };

    const handleFormModalClose = () => {
        setNotificationsGroup(null);
        setNotificationsGroupIndex(-1);
        setShowFormModal(false);
    };

    const handleFormModalOpen = (notificationsGroupData) => {
        if (notificationsGroupData) setNotificationsGroup(notificationsGroupData);
        setShowFormModal(true);
    };

    const handleNotificationsGroupDelete = (data, index) => {
        NotificationService.deleteNotificationByAdmin(data)
            .then((result) => {
                if (result.status === 200) {
                    toast.success('Notifications ont été supprimées avec succès');
                    const notificationsGroupsDATA = [...notificationsGroups];
                    notificationsGroupsDATA.splice(index, 1);
                    setNotificationsGroups(notificationsGroupsDATA);
                    handleDeleteModalClose();
                }
            })
            .catch();
    };

    const submitForm = (values) => {
        NotificationService.createNotificationsByAdmin({
            ...values,
            dateDisplay: values.dateDisplay ? dayjs(values.dateDisplay).format('YYYY-MM-DDTHH:mm:ssZ') : null,
        })
            .then((result) => {
                if (result.status === 200) {
                    toast.success('Notifications ont été enregistrées avec succès');
                    loadNotifications();
                    handleFormModalClose();
                }
            })
            .catch();
    };

    return (
        <>
            <Breadcrumb className="mt-3">
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/' }}>
                    Home
                </Breadcrumb.Item>
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/admin' }}>
                    Admin
                </Breadcrumb.Item>
                <Breadcrumb.Item active>Notifications</Breadcrumb.Item>
            </Breadcrumb>
            <div className="d-flex justify-content-between align-items-center py-2">
                <h2>Notifications</h2>
                <button onClick={() => handleFormModalOpen()} className="btn btn-outline-primary btn-sm">
                    <AiOutlinePlusCircle />
                    <span className="ml-1">New</span>
                </button>
            </div>

            <Table striped bordered hover responsive>
                <thead>
                    <tr>
                        <th className="text-center">Handle</th>
                        <th>Description</th>
                        <th>Title</th>
                        <th className="text-center">Urgency</th>
                        <th className="text-center">Display after</th>
                        <th className="text-center">Count</th>
                        <th className="text-center"></th>
                        <th className="text-center"></th>
                    </tr>
                </thead>
                <tbody>
                    {notificationsGroups.length ? (
                        notificationsGroups.map((notificationsGroupData, i) => {
                            return (
                                <tr key={i}>
                                    <td className="text-center">{notificationsGroupData.data.handle}</td>
                                    <td>{notificationsGroupData.data.description}</td>
                                    <td>{notificationsGroupData.data.title}</td>
                                    <td className="text-center">{notificationsGroupData.data.urgency}</td>
                                    <td className="text-center">
                                        {notificationsGroupData.data.dateDisplay
                                            ? dayjs(notificationsGroupData.data.dateDisplay).format('DD/MM/YYYY HH:mm')
                                            : '-'}
                                    </td>
                                    <td className="text-center">{notificationsGroupData.count}</td>

                                    <td className="text-center">
                                        <FiTrash2
                                            onClick={() => handleDeleteModalOpen(notificationsGroupData, i)}
                                            style={{
                                                fontSize: '16pt',
                                                color: '#cf4e4e',
                                                cursor: 'pointer',
                                            }}
                                        />
                                    </td>
                                    <td className="text-center">
                                        <FiInfo
                                            onClick={() => handleFormModalOpen(notificationsGroupData)}
                                            style={{
                                                fontSize: '16pt',
                                                color: '#8170b8',
                                                cursor: 'pointer',
                                            }}
                                        />
                                    </td>
                                </tr>
                            );
                        })
                    ) : (
                        <tr>
                            <td colSpan="8">no data</td>
                        </tr>
                    )}
                </tbody>
            </Table>
            <CustomModal
                title="Delete Notifications"
                size="sm"
                show={showDeleteModal}
                handleClose={handleDeleteModalClose}
                footerCenter={true}
            >
                <div className="row">
                    <div className="col-md-12 d-flex flex-column align-items-center">
                        <img src="/images/alert.svg" alt="" style={{ width: '100pt' }} className="d-block" />
                        <h4 className="py-3">Irreversible Action</h4>
                        <button
                            className="btn btn-danger  btn-block"
                            onClick={() =>
                                handleNotificationsGroupDelete(notificationsGroup.data, notificationsGroupIndex)
                            }
                        >
                            Delete
                        </button>
                    </div>
                </div>
            </CustomModal>
            <CustomModal title={`New Notification`} size="lg" show={showFormModal} handleClose={handleFormModalClose}>
                <div>
                    <Formik
                        validationSchema={schema}
                        onSubmit={(values) => submitForm(values)}
                        enableReinitialize={true}
                        initialValues={{
                            eventHandle: notificationsGroup ? notificationsGroup.data.handle : '',
                            appId: notificationsGroup ? notificationsGroup.data.appId : CLIAM_APPID,
                            originUserId: notificationsGroup ? notificationsGroup.data.originUserId : user.id,
                            title: notificationsGroup ? notificationsGroup.data.title : '',
                            content: notificationsGroup ? notificationsGroup.data.content : '',
                            actionName: notificationsGroup ? notificationsGroup.data.actionName : undefined,
                            actionUrl: notificationsGroup ? notificationsGroup.data.actionUrl : undefined,
                            urgency: notificationsGroup ? notificationsGroup.data.urgency : 'Normal',
                            byAdmin: notificationsGroup ? notificationsGroup.data.byAdmin : true,
                            dateDisplay: notificationsGroup ? notificationsGroup.data.dateDisplay : null,
                        }}
                    >
                        {({ handleSubmit, handleChange, values, errors, setFieldValue }) => (
                            <Form noValidate onSubmit={handleSubmit}>
                                <Form.Group controlId="exampleForm.events">
                                    <Form.Label>Event</Form.Label>
                                    <Form.Control
                                        disabled={!!notificationsGroup}
                                        as="select"
                                        name="eventHandle"
                                        value={values.eventHandle}
                                        isInvalid={!!errors.eventHandle}
                                        onChange={handleChange}
                                    >
                                        <option value="">Selectionner un event</option>
                                        {events.map((eventData, i) => (
                                            <option key={i} value={eventData.handle}>
                                                {eventData.handle}
                                            </option>
                                        ))}
                                    </Form.Control>
                                </Form.Group>
                                <Form.Group controlId="FormGTitle">
                                    <Form.Label>Title</Form.Label>
                                    <Form.Control
                                        disabled={!!notificationsGroup}
                                        type="text"
                                        placeholder="Title"
                                        name="title"
                                        value={values.title}
                                        onChange={handleChange}
                                        isInvalid={!!errors.title}
                                    />
                                </Form.Group>
                                <Form.Group controlId="FormGContent">
                                    <Form.Label>Content</Form.Label>
                                    <Field name="content">
                                        {({ field }) => (
                                            <ReactQuill
                                                theme="snow"
                                                value={field.value}
                                                onChange={field.onChange(field.name)}
                                                readOnly={!!notificationsGroup}
                                            />
                                        )}
                                    </Field>
                                </Form.Group>
                                <Form.Group controlId="FOrmGActionName">
                                    <Form.Label>Action Name</Form.Label>
                                    <Form.Control
                                        disabled={!!notificationsGroup}
                                        type="text"
                                        placeholder="Action Name"
                                        name="actionName"
                                        value={values.actionName}
                                        onChange={handleChange}
                                        isInvalid={!!errors.actionName}
                                    />
                                </Form.Group>
                                <Form.Group controlId="FormGActionUrl">
                                    <Form.Label>Action Url</Form.Label>
                                    <Form.Control
                                        disabled={!!notificationsGroup}
                                        type="text"
                                        placeholder="Action Url"
                                        name="actionUrl"
                                        value={values.actionUrl}
                                        onChange={handleChange}
                                        isInvalid={!!errors.actionUrl}
                                    />
                                </Form.Group>
                                <Form.Group controlId="exampleForm.urgency">
                                    <Form.Label>Urgency</Form.Label>
                                    <InputGroup>
                                        <UrgencySquareIcon
                                            status={values.urgency}
                                            classNames="mr-2 py-1"
                                            showNormalIcon={true}
                                        />
                                        <Form.Control
                                            disabled={!!notificationsGroup}
                                            as="select"
                                            name="urgency"
                                            value={values.urgency}
                                            isInvalid={!!errors.urgency}
                                            onChange={handleChange}
                                        >
                                            <option value="Critical">Critical</option>
                                            <option value="High">High</option>
                                            <option value="Normal">Normal</option>
                                        </Form.Control>
                                    </InputGroup>
                                </Form.Group>
                                <Form.Group controlId="FormGDateTime">
                                    <Form.Label>DateDisplay</Form.Label>
                                    <DatePickerInputGroup
                                        selectedDate={values.dateDisplay ? new Date(values.dateDisplay) : null}
                                        onChange={(date) => setFieldValue('dateDisplay', date)}
                                        isDisabled={!!notificationsGroup}
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.dateDisplay}</Form.Control.Feedback>
                                </Form.Group>
                                {!notificationsGroup && (
                                    <Button variant="primary" type="submit" block>
                                        Submit
                                    </Button>
                                )}
                            </Form>
                        )}
                    </Formik>
                </div>
            </CustomModal>
        </>
    );
};

export default AdminNotificationsIndex;

const CustomInput = React.forwardRef(({ value, onClick, disabled }, ref) => (
    <Form.Control
        onClick={onClick}
        ref={ref}
        value={value}
        readOnly
        style={{ backgroundColor: disabled ? '#e9ecef' : 'white' }}
        placeholder="DateDisplay"
    />
));

const DatePickerInputGroup = ({ selectedDate, onChange, isDisabled }) => {
    return (
        <InputGroup>
            <DatePicker
                selected={selectedDate}
                onChange={onChange}
                customInput={<CustomInput disabled={isDisabled} />}
                dateFormat="dd/MM/yyyy HH:mm"
                showTimeSelect
                timeFormat="HH:mm"
                timeIntervals={15}
                isClearable={false}
                disabled={isDisabled}
                wrapperClassName="custom-datepicker-wrapper-grow"
            />
            <InputGroup.Append>
                <Button variant="outline-secondary" onClick={() => onChange(null)} className="custom-clear-button">
                    X
                </Button>
            </InputGroup.Append>
        </InputGroup>
    );
};
